// Copyright 2020 Drivetrain Hub LLC
// Shape functions for use with chart.js.
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/prototype
// https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Drawing_shapes

import math from "../math";

/**
 * Cartesian coordinates for a circular arc.
 * @param {number} radius - Radius of circle.
 * @param {number} phiI - Starting polar angle.
 * @param {number} phiF - Ending polar angle.
 * @param {number} xCenter - x-coordinate center of circle.
 * @param {number} yCenter - y-coordinate center of circle.
 * @param {number} num - Number of points.
 * @return {number[][]} - xy coordinate data of circular arc.
 */
export function circleCurve(radius, phiI, phiF, xCenter=0, yCenter=0, num=10) {
  const phiArr = math.linspace(phiI, phiF, num);
  const x = phiArr.map(phi => radius * Math.cos(phi) + xCenter);
  const y = phiArr.map(phi => radius * Math.sin(phi) + yCenter);

  return [x, y]
}

/**
 * Cartesian coordinates for a circle involute arc.
 * @param {number} rBase - Involute base circle radius.
 * @param {number} rollAngleI - Starting involute roll angle.
 * @param {number} rollAngleF - Ending involute roll angle.
 * @param {number} phiBase - Polar angle of involute at its base circle.
 * @param {number} xCenter - x-coordinate center of involute base circle.
 * @param {number} yCenter - y-coordinate center of involute base circle.
 * @param {number} num - Number of points.
 * @return {number[][]}
 */
export function involuteCurve(rBase, rollAngleI, rollAngleF, phiBase=0, xCenter=0, yCenter=0, num=16) {
  const phiI = rollAngleI + phiBase;
  const phiF = rollAngleF + phiBase;
  const phiArr = math.linspace(phiI, phiF, num);

  const x = phiArr.map(phi => rBase * (Math.cos(phi) + (phi - phiBase) * Math.sin(phi)) + xCenter);
  const y = phiArr.map(phi => rBase * (Math.sin(phi) - (phi - phiBase) * Math.cos(phi)) + yCenter);

  return [x, y]
}

/**
 * Cartesian coordinates of an ellipse from a cylinder cross section at an angle relative to the circular section plane.
 * @param {number} cylinderRadius - Radius of cylinder.
 * @param {number} sectionPlaneAngle - Angle of section plane relative to circle section plane, like a gear helix angle.
 * @param {number} phiSectionAxis - Polar angle of section plane axis of rotation.
 * @param {[number]} phiArr - Polar angles of curve.
 * @param {number} xCenter - x-coordinate center of ellipse.
 * @param {number} yCenter - y-coordinate center of ellipse.
 * @return {number[][]}
 */
function ellipseCurve(cylinderRadius, sectionPlaneAngle, phiSectionAxis, phiArr, xCenter=0, yCenter=0) {
  const rMajor = cylinderRadius / Math.cos(sectionPlaneAngle);
  const rMinor = cylinderRadius;
  const phiMajorAxis = phiSectionAxis - Math.PI / 2;

  // notation helpers
  const cosPhiMajor = Math.cos(phiMajorAxis);
  const sinPhiMajor = Math.sin(phiMajorAxis);

  // ellipse at (xCenter, yCenter) with semi-major axis at angle, phiMajorAxis, relative to x-axis
  const x = phiArr.map(phi => rMajor * Math.cos(phi) * cosPhiMajor - rMinor * Math.sin(phi) * sinPhiMajor + xCenter);
  const y = phiArr.map(phi => rMinor * Math.sin(phi) * cosPhiMajor + rMajor * Math.cos(phi) * sinPhiMajor + yCenter);

  return [x, y]
}

/**
 * Cartesian coordinates for an involute tooth tip corner ellipse of right-side tooth centered on x-axis.
 * @param {number} tipCornerRadius - Radius of tooth tip corner in normal plane.
 * @param {number} helixAngle - Helix angle of gear.
 * @param {number} ttrRCenter - Radius to the center of tool tip ellipse.
 * @param {number} ttrLambdaCenter - Polar angle from mid-tooth to the center of tool tip ellipse.
 * @param {number} ttrThetaEoi - Angle in normal plane to end of involute point.  Usually negative without protuberance.
 * @param {number} ttrThetaEndTangent - Angle in normal plane to tangent end point.  Usually negative.
 * @param {number} num - Number of points.
 * @return {number[][]}
 */
export function tipCornerCurve(
  tipCornerRadius, helixAngle, ttrRCenter, ttrLambdaCenter,
  ttrThetaEoi, ttrThetaEndTangent, num=8) {
  ttrLambdaCenter = -ttrLambdaCenter;
  const xCenter = ttrRCenter * Math.cos(ttrLambdaCenter);
  const yCenter = ttrRCenter * Math.sin(ttrLambdaCenter);

  // define polar angles in normal plane, i.e. plane in which tool tip is cylindrical
  const phiProfileTangent = -ttrThetaEoi;
  const phiEndTangent = -ttrThetaEndTangent;
  const phi = math.linspace(phiProfileTangent, phiEndTangent, num);

  return ellipseCurve(tipCornerRadius, helixAngle, 0, phi, xCenter, yCenter);
}
